import { useEffect, useState } from "react";
import { Users, User } from "lucide-react";
import { Modal } from "../../components/Modal";
import { Button, Field, Input, Select, Textarea } from "../../components/ui";
import { createTodo, updateTodo } from "../../api/todos";
import { listEmployees } from "../../api/employees";
import { listBranches } from "../../api/branches";
import { useAuth } from "../../context/AuthContext";
import { unwrapList, fieldErrors, errorMessage } from "../../lib/records";
import { TODO_PRIORITIES, TODO_STATUSES } from "../../lib/enums";

// Creating (§9.1) and editing (§9.4) a daily task.
//
// The two differ more than usual, so the form adapts rather than pretending
// they're the same shape:
//   create — assign to one or many employees at once (`employees[]`, max 200).
//            The API writes one document per person so each history is separate.
//   edit   — a single document, so `employee` is a reassignment to exactly one
//            person. Past completions stay with whoever actually did the work.
//
// `status` here is ACTIVE / PAUSED — whether the task is on the daily list at
// all. It is NOT whether it is done: that is a per-day completion record the
// employee writes from their own app.
const emptyForm = {
  title: "",
  description: "",
  priority: "MEDIUM",
  dueTime: "",
  status: "ACTIVE",
  employees: [],
  employee: "",
};

export default function TodoFormModal({ open, onClose, todo, onSaved, defaultEmployeeId = "" }) {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";

  const [form, setForm] = useState(emptyForm);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const isEdit = !!todo;

  useEffect(() => {
    if (!open) return;
    setForm(
      todo
        ? {
            title: todo.title ?? "",
            description: todo.description ?? "",
            priority: todo.priority ?? "MEDIUM",
            dueTime: todo.dueTime ?? "",
            status: todo.status ?? "ACTIVE",
            employees: [],
            employee: todo.employee?._id ?? todo.employee ?? "",
          }
        : { ...emptyForm, employees: defaultEmployeeId ? [defaultEmployeeId] : [] }
    );
    setErrors({});
    setFormError("");
    setBranchId("");
  }, [open, todo, defaultEmployeeId]);

  // A chain can narrow the assignee list to one branch — the roster across
  // every outlet is too long to scan otherwise.
  useEffect(() => {
    if (!open || !isChain) return;
    listBranches({ limit: 100, status: "ACTIVE" })
      .then((res) => setBranches(unwrapList(res, 100).items))
      .catch(() => setBranches([]));
  }, [open, isChain]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setEmployeesLoading(true);
    listEmployees({
      limit: 100,
      status: "ACTIVE",
      ...(isChain && branchId ? { partner: branchId } : {}),
    })
      .then((res) => !cancelled && setEmployees(unwrapList(res, 100).items))
      .catch(() => !cancelled && setEmployees([]))
      .finally(() => !cancelled && setEmployeesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, branchId, isChain]);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function toggleEmployee(id) {
    setForm((f) => ({
      ...f,
      employees: f.employees.includes(id)
        ? f.employees.filter((e) => e !== id)
        : [...f.employees, id],
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErrors({});
    setFormError("");

    if (!isEdit && form.employees.length === 0) {
      setErrors({ employees: "Pick at least one person to assign this to." });
      return;
    }

    setSaving(true);
    try {
      const base = {
        title: form.title.trim(),
        priority: form.priority,
        status: form.status,
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
      };

      if (isEdit) {
        // §9.4 — `dueTime: null` is how the API clears a time, so an emptied
        // field must send null rather than being omitted.
        await updateTodo(todo._id, {
          ...base,
          dueTime: form.dueTime || null,
          ...(form.employee ? { employee: form.employee } : {}),
        });
      } else {
        await createTodo({
          ...base,
          ...(form.dueTime ? { dueTime: form.dueTime } : {}),
          employees: form.employees,
        });
      }
      onSaved();
    } catch (err) {
      const mapped = fieldErrors(err);
      if (mapped) setErrors(mapped);
      else setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit daily task" : "New daily task"}
      description={
        isEdit
          ? "Changes apply from today onward; past completions are untouched."
          : "A recurring task that appears on each assignee's list every working day."
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {formError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-3 text-sm text-red-700">
            {formError}
          </div>
        )}

        <Field label="Title" required error={errors.title}>
          <Input
            value={form.title}
            onChange={(e) => update("title", e.target.value)}
            placeholder="Clean the popcorn machine"
            maxLength={200}
            required
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <Field label="Description" error={errors.description}>
          <Textarea
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder="Before opening"
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4">
          <Field label="Priority" error={errors.priority}>
            <Select
              value={form.priority}
              onChange={(e) => update("priority", e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            >
              {TODO_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Due time" error={errors.dueTime} hint="Display hint only">
            <Input
              type="time"
              value={form.dueTime}
              onChange={(e) => update("dueTime", e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>

          <Field
            label="On the list"
            error={errors.status}
            hint={form.status === "PAUSED" ? "Hidden, history kept" : "Shows daily"}
          >
            <Select
              value={form.status}
              onChange={(e) => update("status", e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            >
              {TODO_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s === "ACTIVE" ? "Active" : "Paused"}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {/* Assignment. On create this is a multi-select — one task document is
            written per person. On edit it is a single reassignment. */}
        {isChain && branches.length > 0 && !isEdit && (
          <Field label="Filter people by branch">
            <Select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            >
              <option value="">All branches + head office</option>
              {branches.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {isEdit ? (
          <Field
            label="Assigned to"
            error={errors.employee}
            hint="Reassigning moves future days only — past completions stay with whoever did the work."
          >
            <Select
              value={form.employee}
              onChange={(e) => update("employee", e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            >
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.name}
                  {emp.designation ? ` — ${emp.designation}` : ""}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <Field
            label="Assign to"
            required
            error={errors.employees || errors.employee}
            hint={
              form.employees.length > 1
                ? `${form.employees.length} people — each gets their own copy and their own history.`
                : "Pick one person, or several to hand the same task to a team."
            }
          >
            <div className="max-h-44 space-y-1 overflow-y-auto rounded border border-gray-200 p-2">
              {employeesLoading ? (
                <p className="px-1 py-2 text-sm text-gray-500">Loading people…</p>
              ) : employees.length === 0 ? (
                <p className="px-1 py-2 text-sm text-gray-500">No active employees here yet.</p>
              ) : (
                employees.map((emp) => (
                  <label
                    key={emp._id}
                    className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 hover:bg-emerald-50 transition-colors duration-200"
                  >
                    <input
                      type="checkbox"
                      checked={form.employees.includes(emp._id)}
                      onChange={() => toggleEmployee(emp._id)}
                      className="h-4 w-4 accent-emerald-500"
                    />
                    <span className="text-sm text-gray-700">
                      {emp.name}
                      {emp.designation && <span className="text-gray-500"> — {emp.designation}</span>}
                    </span>
                  </label>
                ))
              )}
            </div>
          </Field>
        )}

        {!isEdit && form.employees.length > 1 && (
          <p className="flex items-center gap-2 rounded-md bg-emerald-50/60 px-3 py-2 text-xs text-gray-600">
            <Users size={14} className="shrink-0 text-emerald-600" />
            {form.employees.length} separate tasks will be created — one per person.
          </p>
        )}
        {!isEdit && form.employees.length === 1 && (
          <p className="flex items-center gap-2 rounded-md bg-emerald-50/60 px-3 py-2 text-xs text-gray-600">
            <User size={14} className="shrink-0 text-emerald-600" />
            They'll get a notification as soon as it's assigned.
          </p>
        )}

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={saving}
            className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 w-full sm:w-auto"
          >
            {isEdit ? "Save changes" : "Create task"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
